// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/Dav.tsx
//  Consulta de Pedidos de Venda (DAV). Busca, status, período, cliente,
//  vendedor, ordenação e KPIs rodam NO SERVIDOR.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Trash2, ShoppingCart, PackageCheck, Clock, CheckCircle2,
  Eye, Pencil, ChevronLeft, ChevronRight, Copy, Factory, FileText,
} from 'lucide-react';
import { isAxiosError } from 'axios';
import { toast } from '@/components/ui/sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import { notaFiscalService } from '@/features/fiscal/services/notaFiscalService';
import { pcpService } from '@/features/operacional/services/pcpService';
import { usePermissoes } from '@/contexts/PermissoesContext';
import { FORMULARIOS } from '@/domain/formularios';
import { useDebounce } from '@/hooks/useDebounce';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { BotaoExportar } from '@/components/BotaoExportar';
import { PessoaLookupCombobox } from '@/components/PessoaLookupCombobox';
import { VendedorSearchCombobox } from '@/components/VendedorSearchCombobox';
import { useDav } from '@/features/operacional/hooks/useDav';
import { calcularStatusDav } from '@/features/operacional/types';
import type { StatusDav } from '@/features/operacional/types';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

// ── Helpers de status ─────────────────────────────────────────────────────

const statusConfig: Record<StatusDav, { label: string; className: string; icon: typeof Clock }> = {
  aberto:   { label: 'Aberto',   className: 'bg-blue-500/10 text-blue-500',     icon: Clock        },
  baixado:  { label: 'Baixado',  className: 'bg-yellow-500/10 text-yellow-500', icon: CheckCircle2 },
  entregue: { label: 'Entregue', className: 'bg-green-500/10 text-green-500',   icon: PackageCheck },
};

const PAGE_SIZES = [10, 20, 50, 100] as const;

const BRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// ── Componente ────────────────────────────────────────────────────────────

const Dav = () => {
  const navigate = useNavigate();
  const { temPermissao } = usePermissoes();
  const podeEmitirNfe = temPermissao(FORMULARIOS.DAV_EMITIR_NFE, 'incluir');
  const podeAbrirNfe  = temPermissao(FORMULARIOS.EMISSAO_NFE, 'ler');

  const [searchTerm,   setSearchTerm]    = useState('');
  const [statusFiltro,  setStatusFiltro]  = useState<string>('todos');
  const [clienteId,     setClienteId]     = useState(0);
  const [vendedorId,    setVendedorId]    = useState(0);
  const [deletingId,    setDeletingId]    = useState<number | null>(null);
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);

  // Abre nos últimos 30 dias, mais recentes primeiro: com o tempo a lista inteira fica grande demais.
  const periodo   = usePeriodo('30dias', () => setPagina(1));
  const ordenacao = useOrdenacao<'emissao' | 'numero' | 'cliente' | 'valor'>('emissao', true, () => setPagina(1));
  // Busca e status vão ao servidor (antes filtravam só a página carregada): espera a digitação parar.
  const buscaEnviada = useDebounce(searchTerm.trim(), 400);

  const filtros = {
    busca: buscaEnviada, status: statusFiltro, ...periodo.intervalo,
    clienteId, vendedorId,
    ordenarPor: ordenacao.ordenarPor, decrescente: ordenacao.decrescente,
  };

  const { davs, total, totalPaginas, resumo, isLoading, isFetching, deleteDav, copiarDav, isCopying } =
    useDav({ pagina, tamanhoPagina, filtros });

  // ── KPIs (do servidor: busca + período + cliente + vendedor inteiros, não só a página) ──

  const totalAbertos      = resumo?.abertos      ?? 0;
  const totalBaixados     = resumo?.baixados     ?? 0;
  const totalEntregues    = resumo?.entregues    ?? 0;
  const valorTotalAbertos = resumo?.valorAbertos ?? 0;

  // ── Helpers de paginação ──────────────────────────────────────────────────

  const firstItem = (pagina - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pagina * tamanhoPagina, total);

  const handlePageSizeChange = (value: string) => {
    setTamanhoPagina(Number(value));
    setPagina(1);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Pedidos de Venda (DAV)</h1>
          <p className="text-muted-foreground mt-1">Gerencie os pedidos de venda da empresa</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/operacional/dav/novo')}>
          <Plus className="h-4 w-4" />
          Novo Pedido
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" />Em Aberto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{totalAbertos}</div>
            <p className="text-xs text-muted-foreground mt-1">{BRL(valorTotalAbertos)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />Baixados
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-500">{totalBaixados}</div>
            <p className="text-xs text-muted-foreground mt-1">Pendentes de entrega</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <PackageCheck className="h-4 w-4" />Entregues
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{totalEntregues}</div>
            <p className="text-xs text-muted-foreground mt-1">Concluídos</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <ShoppingCart className="h-4 w-4" />Total Geral
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{total}</div>
            <p className="text-xs text-muted-foreground mt-1">pedidos no filtro</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabela */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <CardTitle>Listagem de Pedidos</CardTitle>
            <div className="flex flex-col sm:flex-row sm:flex-wrap md:justify-end gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Nº, cliente ou CPF/CNPJ..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPagina(1); }}
                  className="pl-9"
                />
              </div>
              <PeriodoFiltro estado={periodo} rotuloData="Emissão" />
              <Select value={statusFiltro} onValueChange={(v) => { setStatusFiltro(v); setPagina(1); }}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="aberto">Aberto</SelectItem>
                  <SelectItem value="baixado">Baixado</SelectItem>
                  <SelectItem value="entregue">Entregue</SelectItem>
                </SelectContent>
              </Select>
              <div className="w-full sm:w-60">
                <PessoaLookupCombobox
                  papel="cliente"
                  value={clienteId || null}
                  onChange={(id) => { setClienteId(id ?? 0); setPagina(1); }}
                  placeholder="Todos os clientes"
                />
              </div>
              <div className="w-full sm:w-56">
                <VendedorSearchCombobox
                  value={vendedorId || null}
                  onChange={(id) => { setVendedorId(id ?? 0); setPagina(1); }}
                  placeholder="Todos os vendedores"
                />
              </div>
              <BotaoExportar endpoint="/api/dav/exportar" params={filtros} />
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>Nº DAV</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="cliente" ordenacao={ordenacao} decrescentePadrao={false}>Cliente</CabecalhoOrdenavel>
                  <TableHead>Vendedor</TableHead>
                  <CabecalhoOrdenavel coluna="emissao" ordenacao={ordenacao}>Emissão</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="valor" ordenacao={ordenacao} className="text-right">Total Líquido</CabecalhoOrdenavel>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Carregando...
                    </TableCell>
                  </TableRow>
                ) : davs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      {periodo.periodo !== 'todos'
                        ? 'Nenhum pedido neste período — amplie o período para ver os anteriores'
                        : 'Nenhum pedido encontrado'}
                    </TableCell>
                  </TableRow>
                ) : (
                  davs.map((dav) => {
                    const status = calcularStatusDav(dav.isBaixada, dav.isPedidoEntregue);
                    const cfg    = statusConfig[status];
                    const Icon   = cfg.icon;
                    return (
                      <TableRow key={dav.id}>
                        <TableCell className="font-medium">#{dav.numeroDav}</TableCell>
                        <TableCell>
                          {dav.nomeRazao || <span className="text-muted-foreground">—</span>}
                          {dav.cpfCnpj && <div className="text-xs text-muted-foreground">{dav.cpfCnpj}</div>}
                        </TableCell>
                        <TableCell>
                          {dav.vendedorNome || <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell>
                          {new Date(dav.dtEmissao).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {BRL(dav.totalLiquido ?? 0)}
                        </TableCell>
                        <TableCell>
                          <Badge className={cfg.className}>
                            <span className="flex items-center gap-1">
                              <Icon className="h-3 w-3" />
                              {cfg.label}
                            </span>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {!dav.isBaixada && (
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => navigate(`/operacional/dav/${dav.id}`)}
                                title="Editar"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              size="sm" variant="ghost"
                              title="Ver detalhes"
                              onClick={() => navigate(`/operacional/dav/${dav.id}`)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              title="Copiar pedido"
                              disabled={isCopying}
                              onClick={async () => {
                                const copia = await copiarDav(dav.id);
                                navigate(`/operacional/dav/${copia.id}`);
                              }}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm" variant="ghost"
                              title="Gerar ordem de produção deste pedido"
                              onClick={async () => {
                                try {
                                  const ordem = await pcpService.gerarDePedido(dav.id);
                                  toast.success(`Ordem de Produção nº ${ordem.numeroOrdem} gerada.`);
                                  navigate(`/operacional/pcp/${ordem.id}`);
                                } catch (err) {
                                  toast.error('Não foi possível gerar a ordem de produção.', {
                                    description: getApiErrorMessage(err),
                                  });
                                }
                              }}
                            >
                              <Factory className="h-4 w-4" />
                            </Button>
                            {podeEmitirNfe && (
                              <Button
                                size="sm" variant="ghost"
                                title="Emitir NF-e deste pedido"
                                onClick={async () => {
                                  // Só pedido baixado fatura: é a baixa que dá saída no estoque e gera o contas a receber.
                                  if (!dav.isBaixada) {
                                    toast.info('Baixe o pedido antes de emitir a NF-e.', {
                                      description: 'É a baixa que dá saída no estoque e gera o contas a receber.',
                                      action: { label: 'Abrir pedido', onClick: () => navigate(`/operacional/dav/${dav.id}`) },
                                    });
                                    return;
                                  }
                                  try {
                                    const nota = await notaFiscalService.gerarDePedido(dav.id);
                                    // Sem acesso à tela de Emissão NF-e, o rascunho fica para quem transmite.
                                    if (!podeAbrirNfe) {
                                      toast.success('NF-e gerada em rascunho.', {
                                        description: 'Seu perfil não acessa a Emissão NF-e — a transmissão fica com quem tem esse acesso.',
                                        duration: 15000,
                                      });
                                      return;
                                    }
                                    if (nota.avisos.length > 0) {
                                      toast.warning('NF-e gerada em rascunho — confira antes de transmitir.', {
                                        description: nota.avisos.join(' • '),
                                        duration: 15000,
                                      });
                                    } else {
                                      toast.success('NF-e gerada em rascunho. Revise e transmita.');
                                    }
                                    navigate(`/fiscal/notas-fiscais/${nota.id}`);
                                  } catch (err) {
                                    const notaId = isAxiosError(err) && err.response?.status === 409
                                      ? (err.response.data as { notaId?: number })?.notaId
                                      : undefined;
                                    toast.error('Não foi possível gerar a NF-e.', {
                                      description: getApiErrorMessage(err),
                                      action: notaId && podeAbrirNfe
                                        ? { label: 'Abrir nota', onClick: () => navigate(`/fiscal/notas-fiscais/${notaId}`) }
                                        : undefined,
                                    });
                                  }
                                }}
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                            )}
                            {!dav.isBaixada && (
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => setDeletingId(dav.id)}
                                title="Excluir"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* ── Rodapé de paginação ───────────────────────────────────── */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0
                ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
              {isFetching && !isLoading && (
                <span className="ml-2 text-xs">(atualizando...)</span>
              )}
            </p>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  Itens por página:
                </span>
                <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => (
                      <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline" size="icon" className="h-8 w-8"
                  onClick={() => setPagina(1)}
                  disabled={pagina === 1}
                >
                  «
                </Button>
                <Button
                  variant="outline" size="icon" className="h-8 w-8"
                  onClick={() => setPagina((p) => p - 1)}
                  disabled={pagina === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)         page = i + 1;
                  else if (pagina <= 3)                page = i + 1;
                  else if (pagina >= totalPaginas - 2) page = totalPaginas - 4 + i;
                  else                                 page = pagina - 2 + i;
                  return (
                    <Button
                      key={page}
                      variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs"
                      onClick={() => setPagina(page)}
                    >
                      {page}
                    </Button>
                  );
                })}

                <Button
                  variant="outline" size="icon" className="h-8 w-8"
                  onClick={() => setPagina((p) => p + 1)}
                  disabled={pagina >= totalPaginas}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline" size="icon" className="h-8 w-8"
                  onClick={() => setPagina(totalPaginas)}
                  disabled={pagina >= totalPaginas}
                >
                  »
                </Button>
              </div>

              <span className="text-sm text-muted-foreground whitespace-nowrap">
                Página {pagina} de {totalPaginas}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Confirmar exclusão */}
      <DeleteConfirmDialog
        open={!!deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        onConfirm={() => {
          if (deletingId) {
            deleteDav(deletingId);
            setDeletingId(null);
          }
        }}
        title="Excluir Pedido de Venda"
        description="Tem certeza que deseja excluir este pedido? Pedidos já baixados não podem ser excluídos."
      />
    </div>
  );
};

export default Dav;
